document.addEventListener("DOMContentLoaded", () => {
  customElements.define("collection-tab", CollectionTab);
  customElements.define(
    "collection-tab-infinite-loader",
    CollectionTabInfiniteLoader
  );
});

function DOMParserHelper(htmlString) {
  const parser = new DOMParser();
  return parser.parseFromString(htmlString, "text/html");
}

function getRemainingProducts(grid) {
  const total = parseInt(grid.dataset.productsLeft || "0");

  const loaded = grid.querySelectorAll(
    ":scope > *:not(collection-tab-infinite-loader)"
  ).length;

  let remaining = total - loaded;
  if (remaining < 0) remaining = 0;

  const remainingEl = grid.querySelector("[data-remaining-count]");
  if (remainingEl) {
    remainingEl.textContent = new Intl.NumberFormat().format(remaining);
  }
}

function debounce(fn, delay = 200) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/* ===============================
   TAB COMPONENT
================================== */

class CollectionTab extends HTMLElement {
  connectedCallback() {
    this.collectionHandle = this.dataset.collectionHandle;
    this.collectionId = this.dataset.collectionId;

    this.url = `/collections/${this.collectionHandle}?section_id=collection-tabs-ajax`;

    this.addEventListener("click", () => this.activate());
    this.addEventListener("keydown", (e) => this.handleKeydown(e));
  }

  handleKeydown(e) {
    const tabs = [...this.parentElement.querySelectorAll('[role="tab"]')];
    const currentIndex = tabs.indexOf(this);

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        tabs[(currentIndex + 1) % tabs.length].focus();
        break;

      case "ArrowLeft":
        e.preventDefault();
        tabs[(currentIndex - 1 + tabs.length) % tabs.length].focus();
        break;

      case "Home":
        e.preventDefault();
        tabs[0].focus();
        break;

      case "End":
        e.preventDefault();
        tabs[tabs.length - 1].focus();
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        this.activate();
        break;
    }
  }
  
  async activate() {
    if (this.classList.contains("collection-tabs__tab--active")) return;

    const activeTab = document.querySelector(
      ".collection-tabs__tab--active"
    );

    const activePanel = document.querySelector(
      ".collection-tabs__product-grid--active"
    );

    /* ===============================
      DEACTIVATE CURRENT TAB + PANEL
    ================================== */

    if (activeTab) {
      activeTab.classList.remove("collection-tabs__tab--active");
      activeTab.setAttribute("aria-selected", "false");
      activeTab.setAttribute("tabindex", "-1");
    }

    if (activePanel) {
      activePanel.classList.remove("collection-tabs__product-grid--active");
      activePanel.setAttribute("aria-hidden", "true");

      // Unobserve its loader
      const oldLoader = activePanel.querySelector(
        "collection-tab-infinite-loader"
      );
      if (oldLoader) {
        CollectionTabInfiniteLoader.unobserve(oldLoader);
      }

      // Create cache storage if not exists
      if (!activePanel._cachedFragment) {
        activePanel._cachedFragment = document.createDocumentFragment();
      }

      // Move product nodes into fragment (preserves state)
      [...activePanel.children].forEach(child => {
        if (child.tagName !== "COLLECTION-TAB-INFINITE-LOADER") {
          activePanel._cachedFragment.appendChild(child);
        }
      });
    }

    /* ===============================
      ACTIVATE NEW TAB + PANEL
    ================================== */

    this.classList.add("collection-tabs__tab--active");
    this.setAttribute("aria-selected", "true");
    this.setAttribute("tabindex", "0");

    const panel = document.querySelector(
      `.collection-tabs__product-grid[data-collection-id="${this.collectionId}"]`
    );

    if (!panel) return;

    panel.classList.add("collection-tabs__product-grid--active");
    panel.setAttribute("aria-hidden", "false");

    /* ===============================
      RESTORE CACHED PRODUCTS
    ================================== */

    if (panel._cachedFragment && panel._cachedFragment.childNodes.length > 0) {
      const loader = panel.querySelector(
        "collection-tab-infinite-loader"
      );
      panel.insertBefore(panel._cachedFragment, loader);
    }

    /* ===============================
      REATTACH INFINITE OBSERVER
    ================================== */

    const loader = panel.querySelector(
      "collection-tab-infinite-loader"
    );

    if (loader) {
      CollectionTabInfiniteLoader.observe(loader);
    }

    /* ===============================
      UPDATE REMAINING COUNT
    ================================== */

    getRemainingProducts(panel);
  }


  setupInfiniteObserver(panel) {
    // Remove observers from all loaders
    document.querySelectorAll("collection-tab-infinite-loader")
      .forEach(loader => {
        CollectionTabInfiniteLoader.unobserve(loader);
      });

    const loader = panel.querySelector("collection-tab-infinite-loader");
    if (!loader) return;

    CollectionTabInfiniteLoader.observe(loader);
  }

}

/* ===============================
  INFINITE LOADER
================================== */
class CollectionTabInfiniteLoader extends HTMLElement {
  static observer;

  static observe(el) {
    if (!this.observer) return;
    this.observer.observe(el);
  }

  static unobserve(el) {
    if (!this.observer) return;
    this.observer.unobserve(el);
  }


  connectedCallback() {
    if (!CollectionTabInfiniteLoader.observer) {
      CollectionTabInfiniteLoader.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            // console.log(this);
            if (!entry.isIntersecting) return;
            const loader = entry.target;
            // debounce per loader instance
            if (!loader._debouncedLoad) {
              loader._debouncedLoad = debounce(() => {
                loader.loadMore();
              }, 300);
            }
            loader._debouncedLoad();
          });
        },
        {
          root: null,
          rootMargin: "200% 0px",
          threshold: 0
        }
      );
    }

    // Only observe if inside active grid
    if (this.closest(".collection-tabs__product-grid--active")) {
      CollectionTabInfiniteLoader.observer.observe(this);
    }
  }

  disconnectedCallback() {
    if (CollectionTabInfiniteLoader.observer) {
      CollectionTabInfiniteLoader.observer.unobserve(this);
    }
  }

  async loadMore() {
    // console.log(this);
    this.classList.remove('collection-tab-infinite-loader--disabled');
    const grid = this.closest(".collection-tabs__product-grid");
    if (!grid) return;

    if (grid.dataset.loading === "true") return;

    const handle = grid.dataset.collectionHandle;
    const collectionId = grid.dataset.collectionId;

    let currentPage = parseInt(grid.dataset.page || "1");
    let nextPage = currentPage + 1;

    grid.dataset.loading = "true";
    // console.log(grid.childElementCount, "childELementCount");
    try {
      const response = await fetch(
        `/collections/${handle}?page=${nextPage}&section_id=collection-tabs-ajax`
      );

      if (!response.ok) throw new Error("Network error");

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const newGrid = doc.querySelector(
        `[data-api-collection-tabs]`
      );
      
      if (!newGrid) {
        CollectionTabInfiniteLoader.observer.unobserve(this);
        return;
      }
      const fetchedPage = newGrid.dataset?.page

      const products = [...newGrid.children].filter(
        el => el.tagName !== "COLLECTION-TAB-INFINITE-LOADER"
      );

      if (this.dataset.totalPages == fetchedPage) {
        CollectionTabInfiniteLoader.observer.unobserve(this);
        this.remove();
        return;
      }

      const fragment = document.createDocumentFragment();
      products.forEach(p => fragment.appendChild(p));

      grid.insertBefore(fragment, this);

      this.classList.add('collection-tab-infinite-loader--disabled');

      getRemainingProducts(grid);

      grid.dataset.page = nextPage;
      setTimeout(function(){ HulkappWishlist?._manageCollectionWL(); }, 800);
    } catch (err) {
      console.error("Infinite load failed:", err);
      CollectionTabInfiniteLoader.observer.unobserve(this);
    }

    grid.dataset.loading = "false";
    
    setTimeout(() => {
      if (typeof HulkappWishlist !== 'undefined' && HulkappWishlist) {
        HulkappWishlist._manageCollectionWL();


        allHulkWishlistButtons = Array.from(document.querySelectorAll('[data-wishlist]'));
        HulkappWishlist._checkForProductStatus(allHulkWishlistButtons, 'multiple');
      }  
    }, 1500);
  }
}